import { apiClient } from '_app'
import {
  ApiResponse,
  MichiganSOSBundleDto,
  MichiganSOSBundleTitleApplicationDeliveryPhotoDto,
  MichiganTitleApplicationCoverLetterDto,
  NeedsToBeTakenToSecretaryOfStateDetailsDto,
  NeedsToBeTakenToSecretaryOfStateTitleApplicationDeliveryPhotoDto,
  VehicleDto
} from 'dtos'
import { MichiganSOSBundlesParameters } from 'parameters/michiganSOSBundlesParameters'
import { base64ToArrayBuffer, URIQuery } from 'utils'

export const getMichiganSOSBundles = (parameters: MichiganSOSBundlesParameters) =>
  apiClient
    .get<ApiResponse<MichiganSOSBundleDto[]>>('/api/michigan-sos-bundles' + URIQuery({ ...parameters }))
    .then(response => response.data)

export const getMichiganSOSBundle = (michiganSOSBundleGuid: string) =>
  apiClient
    .get<ApiResponse<MichiganSOSBundleDto>>(`/api/michigan-sos-bundles/${encodeURIComponent(michiganSOSBundleGuid)}`)
    .then(response => response.data)

export const createMichiganSOSBundle = (values: MichiganSOSBundleDto) =>
  apiClient.post<ApiResponse<MichiganSOSBundleDto>>('/api/michigan-sos-bundles', values).then(response => response.data)

export const updateMichiganSOSBundle = (values: MichiganSOSBundleDto) =>
  apiClient.put<ApiResponse<MichiganSOSBundleDto>>('/api/michigan-sos-bundles', values).then(response => response.data)

export const deleteMichiganSOSBundle = (michiganSOSBundleGuid: string) =>
  apiClient
    .delete<ApiResponse<void>>(`/api/michigan-sos-bundles/${encodeURIComponent(michiganSOSBundleGuid)}`)
    .then(response => response.data)

export const generateTitleApplicationCoverLetter = (michiganSOSBundleGuid: string) =>
  apiClient
    .get(`/api/michigan-sos-bundles/${encodeURIComponent(michiganSOSBundleGuid)}/generate-title-application-cover-letter`, {
      responseType: 'blob'
    })
    .then(res => {
      const file = new Blob([res.data], { type: 'application/pdf' })
      const fileUrl = window.URL.createObjectURL(file)
      window.open(fileUrl)
    })

export const lockMichiganSOSBundle = (michiganSOSBundleGuid: string) =>
  apiClient
    .put<ApiResponse<MichiganSOSBundleDto>>(`/api/michigan-sos-bundles/${encodeURIComponent(michiganSOSBundleGuid)}/lock`)
    .then(res => res.data)

export const unlockMichiganSOSBundle = (michiganSOSBundleGuid: string) =>
  apiClient
    .put<ApiResponse<MichiganSOSBundleDto>>(`/api/michigan-sos-bundles/${encodeURIComponent(michiganSOSBundleGuid)}/unlock`)
    .then(res => res.data)

export const getMichiganSOSBundleVehicles = (michiganSOSBundleGuid: string, parameters: MichiganSOSBundlesParameters) =>
  apiClient
    .get<ApiResponse<VehicleDto[]>>(
      `/api/michigan-sos-bundles/${encodeURIComponent(michiganSOSBundleGuid)}/vehicles` + URIQuery({ ...parameters })
    )
    .then(response => response.data)

export const addVehiclesToMichiganSOSBundle = (michiganSOSBundleGuid: string, values: MichiganSOSBundleDto[]) =>
  apiClient
    .post<ApiResponse<MichiganSOSBundleDto>>(
      `/api/michigan-sos-bundles/${encodeURIComponent(michiganSOSBundleGuid)}/vehicles`,
      values
    )
    .then(response => response.data)

export const removeVehiclesFromMichiganSOSBundle = (michiganSOSBundleGuid: string, values: VehicleDto[]) =>
  apiClient
    .delete<ApiResponse<MichiganSOSBundleDto>>(
      `/api/michigan-sos-bundles/${encodeURIComponent(michiganSOSBundleGuid)}/vehicles`,
      { data: values }
    )
    .then(response => response.data)

export const getMichiganSOSBundleTitleApplicationDeliveryPhotoBase64 = (michiganSOSBundleGuid: string, fileName: string) =>
  apiClient
    .get<string>(
      `/api/michigan-sos-bundles/${encodeURIComponent(
        michiganSOSBundleGuid
      )}/title-application-delivery-photo/${encodeURIComponent(fileName)}`
    )
    .then(response => response.data)

export const getMichiganSOSBundleTitleApplicationDeliveryPhoto = (michiganSOSBundleGuid: string) =>
  apiClient
    .get<ApiResponse<MichiganSOSBundleTitleApplicationDeliveryPhotoDto>>(
      `/api/michigan-sos-bundles/${encodeURIComponent(
        michiganSOSBundleGuid
      )}/michigan-sos-bundle-title-application-delivery-photo`
    )
    .then(response => response.data)

export const getMichiganSOSBundleTitleApplicationDeliveryFile = (michiganSOSBundleGuid: string, fileName: string) =>
  apiClient
    .get(
      `/api/michigan-sos-bundles/${encodeURIComponent(
        michiganSOSBundleGuid
      )}/michigan-sos-bundle-title-application-delivery-photo/${encodeURIComponent(fileName)}`
    )
    .then(response => {
      const buffer = base64ToArrayBuffer(response.data)
      const file = new Blob([buffer], { type: 'image/png' })
      const fileUrl = window.URL.createObjectURL(file)
      const _newTab = window.open(fileUrl)
    })

export const deleteMichiganSOSBundleTitleApplicationDeliveryPhoto = (michiganSOSBundleGuid: string) =>
  apiClient
    .delete<ApiResponse<MichiganSOSBundleTitleApplicationDeliveryPhotoDto>>(
      `/api/michigan-sos-bundles/${encodeURIComponent(
        michiganSOSBundleGuid
      )}/michigan-sos-bundle-title-application-delivery-photo`
    )
    .then(response => response.data)

export const getTitleApplicationDeliveryPhoto = (michiganSoSBundleGuid: string) =>
  apiClient
    .get<ApiResponse<MichiganSOSBundleTitleApplicationDeliveryPhotoDto>>(
      `/api/michigan-sos-bundles/${encodeURIComponent(michiganSoSBundleGuid)}/title-application-delivery-photo`
    )
    .then(res => res.data)

export const uploadReplaceTitleApplicationDeliveryPhoto = (
  michiganSoSBundleGuid: string,
  values: MichiganSOSBundleTitleApplicationDeliveryPhotoDto
) => {
  const payload = new FormData()

  if (values.titleApplicationDeliveryPhotoFile) {
    payload.append('titleApplicationDeliveryPhotoFile', values.titleApplicationDeliveryPhotoFile)
  }

  return apiClient<ApiResponse<MichiganSOSBundleTitleApplicationDeliveryPhotoDto>>({
    data: payload,
    headers: { 'Content-Type': 'multipart/form-data' },
    method: 'put',
    url: `/api/michigan-sos-bundles/${encodeURIComponent(michiganSoSBundleGuid)}/title-application-delivery-photo`
  }).then(res => res.data)
}

export const getMichiganSOSBundlesTitleApplicationCoverLetterInformation = (michiganSOSBundleGuid: string) =>
  apiClient
    .get<ApiResponse<MichiganTitleApplicationCoverLetterDto>>(
      `/api/michigan-sos-bundles/${encodeURIComponent(michiganSOSBundleGuid)}/title-application-cover-letter`
    )
    .then(response => response.data)

export const updateMichiganSOSBundlesTitleApplicationCoverLetterInformation = (
  michiganSOSBundleGuid: string,
  values: MichiganTitleApplicationCoverLetterDto
) =>
  apiClient
    .put<ApiResponse<MichiganTitleApplicationCoverLetterDto>>(
      `/api/michigan-sos-bundles/${encodeURIComponent(michiganSOSBundleGuid)}/title-application-cover-letter`,
      values
    )
    .then(res => res.data)

export const getNeedsToBeTakenToSecretaryOfStateDetails = (michiganSOSBundleId: string) =>
  apiClient
    .get<ApiResponse<NeedsToBeTakenToSecretaryOfStateDetailsDto>>(
      `/api/michigan-sos-bundles/${encodeURIComponent(michiganSOSBundleId)}/needs-to-be-taken-to-secretary-of-state`
    )
    .then(response => response.data)

export const updateNeedsToBeTakenToSecretaryOfStateDetails = (
  michiganSOSBundleGuid: string,
  values: NeedsToBeTakenToSecretaryOfStateDetailsDto
) =>
  apiClient
    .put<ApiResponse<NeedsToBeTakenToSecretaryOfStateDetailsDto>>(
      `/api/michigan-sos-bundles/${encodeURIComponent(michiganSOSBundleGuid)}/needs-to-be-taken-to-secretary-of-state`,
      values
    )
    .then(response => response.data)

export const getNeedsToBeTakenToSecretaryOfStatePhotoBase64 = (michiganSOSBundleGuid: string, fileName: string) =>
  apiClient
    .get<string>(
      `/api/michigan-sos-bundles/${encodeURIComponent(
        michiganSOSBundleGuid
      )}/needs-to-be-taken-to-secretary-of-state/${encodeURIComponent(fileName)}`
    )
    .then(response => response.data)

export const getNeedsToBeTakenToSecretaryOfStateTitleApplicationDeliveryPhotoInformation = (michiganSOSBundleGuid: string) =>
  apiClient
    .get<ApiResponse<NeedsToBeTakenToSecretaryOfStateTitleApplicationDeliveryPhotoDto>>(
      `/api/michigan-sos-bundles/${encodeURIComponent(
        michiganSOSBundleGuid
      )}/needs-to-be-taken-to-secretary-of-state/title-application-delivery-photo`
    )
    .then(response => response.data)

export const updateNeedsToBeTakenToSecretaryOfStateTitleApplicationDeliveryPhoto = (
  michiganSOSBundleGuid: string,
  values: NeedsToBeTakenToSecretaryOfStateTitleApplicationDeliveryPhotoDto
) => {
  const payload = new FormData()

  if (values.titleApplicationDeliveryPhotoFile) {
    payload.append('titleApplicationDeliveryPhotoFile', values.titleApplicationDeliveryPhotoFile)
  }

  return apiClient<ApiResponse<NeedsToBeTakenToSecretaryOfStateTitleApplicationDeliveryPhotoDto>>({
    data: payload,
    headers: { 'Content-Type': 'multipart/form-data' },
    method: 'put',
    url: `/api/michigan-sos-bundles/${encodeURIComponent(
      michiganSOSBundleGuid
    )}/needs-to-be-taken-to-secretary-of-state/title-application-delivery-photo`
  }).then(response => response.data)
}

export const deleteNeedsToBeTakenToSecretaryOfStateTitleApplicationDeliveryPhoto = (michiganSOSBundleGuid: string) =>
  apiClient
    .delete<ApiResponse<NeedsToBeTakenToSecretaryOfStateTitleApplicationDeliveryPhotoDto>>(
      `/api/michigan-sos-bundles/${encodeURIComponent(
        michiganSOSBundleGuid
      )}/needs-to-be-taken-to-secretary-of-state/title-application-delivery-photo`
    )
    .then(response => response.data)

export const getOwners = () => apiClient.get<ApiResponse<string[]>>(`/api/michigan-sos-bundles/owners`).then(res => res.data)
